import { MARKDOWN_HEADER_REGEX, MARKDOWN_UNDERLINE_HEADER_REGEX } from '@commutifi-fe/constants';
import { type SerializedHeadingNode } from '@lexical/rich-text';
import { type SerializedLexicalNode, type SerializedTextNode } from 'lexical';
import slugifyOriginal from 'slugify';

/**
 * Extract markdown headers from a string.
 * @param content - The string to extract headers from.
 * @returns An array of objects with the header level and text.
 */
export function extractMarkdownHeaders(content: string, options: {
  /**
   * Whether to extract only the top level headers.
   */
  extractOnlyTopLevelHeaders?: boolean;
} = {}) {
  // Extract headers with # style and underline style (=== or ---)
  const allHeaders: {
    level: number;
    text: string;
  }[] = [];

  // First, handle hash-style headers
  let match;
  while ((match = MARKDOWN_HEADER_REGEX.exec(content)) !== null) {
    const level = match[1].length;
    const text = match[2].trim();
    allHeaders.push({
      level,
      text
    });
  }

  // Then, handle underline-style headers
  while ((match = MARKDOWN_UNDERLINE_HEADER_REGEX.exec(content)) !== null) {
    // === is level 1 (h1), --- is level 2 (h2)
    const level = match[2] === '=' ? 1 : 2;
    const text = match[1].trim();
    allHeaders.push({
      level,
      text
    });
  }
  if (options.extractOnlyTopLevelHeaders) {
    // Find the minimum header level (top level)
    const minLevel = allHeaders.length > 0 ? Math.min(...allHeaders.map(h => h.level)) : 0;

    // Filter to only include the top level headers
    return allHeaders.filter(h => h.level === minLevel);
  }
  return allHeaders;
}

/**
 * Cleans header text by removing markdown formatting
 * @param text The header text to clean
 * @returns The cleaned text without markdown formatting
 */
export const cleanMarkdownHeaderText = (text: string): string => {
  // Remove bold markdown (**text** or __text__)
  let cleanedText = text.replace(/(\*\*|__)(.*?)\1/g, '$2');

  // Remove italic markdown (*text* or _text_)
  cleanedText = cleanedText.replace(/(\*|_)(.*?)\1/g, '$2');

  // Remove code blocks (`text`)
  cleanedText = cleanedText.replace(/`(.*?)`/g, '$1');

  // Remove link text part from markdown links [text](url)
  cleanedText = cleanedText.replace(/\[(.*?)\]\(.*?\)/g, '$1');

  // Remove HTML tags
  cleanedText = cleanedText.replace(/<\/?[^>]+(>|$)/g, '');

  // Remove any extra whitespace
  cleanedText = cleanedText.trim();
  return cleanedText;
};

/**
 * Extract headings from a Lexical serialized JSON string.
 * Walks the node tree recursively to find heading nodes.
 *
 * COUPLING: Text extraction here replicates Lexical's node.getTextContent() algorithm.
 * HeadingAnchorPlugin (packages/ui) uses the real getTextContent() to set heading element IDs.
 * Both paths must produce identical text for the same node structure, or anchor links break.
 * The slug consistency tests in text.test.ts verify this for representative cases.
 */
export function extractLexicalHeadings(jsonString: string, options: {
  extractOnlyTopLevelHeaders?: boolean;
} = {}): {
  level: number;
  text: string;
}[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonString);
  } catch {
    return [];
  }
  if (!isLexicalSerializedRoot(parsed)) {
    return [];
  }
  const {
    root
  } = parsed;
  const headings: {
    level: number;
    text: string;
  }[] = [];
  function getTextContent(node: LexicalNode): string {
    if (node.type === 'linebreak') {
      return '\n';
    }
    if (typeof node.text === 'string') {
      return node.text;
    }
    if (Array.isArray(node.children)) {
      return node.children.map(getTextContent).join('');
    }
    return '';
  }
  function walk(node: LexicalNode): void {
    if (node.type === 'heading' && node.tag) {
      const level = parseInt(node.tag.slice(1), 10);
      if (!isNaN(level) && level >= 1 && level <= 6) {
        const text = Array.isArray(node.children) ? node.children.map(getTextContent).join('') : '';
        headings.push({
          level,
          text
        });
      }
    }
    if (Array.isArray(node.children)) {
      node.children.forEach(walk);
    }
  }
  root.children.forEach(walk);
  if (options.extractOnlyTopLevelHeaders && headings.length > 0) {
    const minLevel = Math.min(...headings.map(h => h.level));
    return headings.filter(h => h.level === minLevel);
  }
  return headings;
}
export function slugify(text: string, options: Parameters<typeof slugifyOriginal>[1] = {}) {
  return slugifyOriginal(text, typeof options === 'object' ? {
    lower: true,
    trim: true,
    ...options
  } : options);
}

// Pick only the fields we need from Lexical's serialized types.
// Full types require many fields (version, direction, format, indent) irrelevant
// for read-only heading extraction.
type LexicalNode = Pick<SerializedLexicalNode, 'type'> & Partial<Pick<SerializedHeadingNode, 'tag'>> & Partial<Pick<SerializedTextNode, 'text'>> & {
  children?: LexicalNode[];
};
type LexicalSerializedRoot = {
  root: {
    children: LexicalNode[];
  };
};
function isLexicalSerializedRoot(value: unknown): value is LexicalSerializedRoot {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.root !== 'object' || obj.root === null) {
    return false;
  }
  const root = obj.root as Record<string, unknown>;
  return Array.isArray(root.children);
}